import React, { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatMedida, formatNum, unidadeLabel, STATUS_META } from "@/lib/format";
import { sharePdf } from "@/lib/share";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AlertTriangle, Boxes, Scale, Layers, Loader2, Printer, FileText, Share2 } from "lucide-react";
import { toast } from "sonner";

function StatCard({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="flex items-center gap-3 rounded-md border-2 border-slate-200 bg-white p-4" data-testid={testid}>
      <div className="flex h-11 w-11 items-center justify-center rounded-md bg-slate-100 text-slate-700">
        <Icon size={22} />
      </div>
      <div>
        <div className="text-2xl font-bold tracking-tight text-slate-900 font-mono">{value}</div>
        <div className="text-xs font-medium text-slate-500">{label}</div>
        {sub && <div className="text-xs font-medium text-slate-400">{sub}</div>}
      </div>
    </div>
  );
}

const FILTROS = [
  { v: "todos", label: "Todos" },
  { v: "saldo", label: "Só com saldo (Disponíveis)" },
  { v: "zerados", label: "Só zerados" },
];

export default function Painel() {
  const { hasPerm } = useAuth();
  const canReport = hasPerm("estoque:pdf");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [rolos, setRolos] = useState([]);
  const [loadingRolos, setLoadingRolos] = useState(false);
  const [reportFiltro, setReportFiltro] = useState("todos");
  const [showReport, setShowReport] = useState(false);
  const [fFornecedor, setFFornecedor] = useState("");
  const [fTipo, setFTipo] = useState("");
  const [fCor, setFCor] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [sort, setSort] = useState("alerta");

  const load = useCallback(() => {
    setLoading(true);
    api
      .get("/painel")
      .then((res) => setData(res.data))
      .catch((e) => {const _m=apiError(e); if(_m) toast.error(_m);})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openDetail = async (card) => {
    setSelected(card);
    setLoadingRolos(true);
    try {
      const res = await api.get(`/combinacoes/${card.combinacao_id}/rolos`, { params: { status: "Disponível" } });
      setRolos(res.data);
    } catch (e) {
      {const _m=apiError(e); if(_m) toast.error(_m);};
    } finally {
      setLoadingRolos(false);
    }
  };

  const cards = data?.cards || [];
  const resumo = data?.resumo || {};

  const tipos = [...new Set(cards.map((c) => c.tipo))].sort((a, b) => a.localeCompare(b));
  const cores = [...new Set(cards.map((c) => c.cor))].sort((a, b) => a.localeCompare(b));
  const fornecedores = [...new Map(cards.map((c) => [c.fornecedor_id, c.fornecedor_nome])).entries()].filter(([id]) => id);

  const rank = (s) => ({ zerado: 0, baixo: 1, atencao: 2, ok: 3 }[s] ?? 4);
  const cardsView = cards
    .filter((c) =>
      (!fFornecedor || c.fornecedor_id === fFornecedor) &&
      (!fTipo || c.tipo === fTipo) &&
      (!fCor || c.cor === fCor) &&
      (!fStatus || c.status === fStatus))
    .sort((a, b) => {
      if (sort === "maior") return b.peso_total_kg - a.peso_total_kg;
      if (sort === "menor") return a.peso_total_kg - b.peso_total_kg;
      if (sort === "az") return `${a.tipo} ${a.cor}`.localeCompare(`${b.tipo} ${b.cor}`);
      return rank(a.status) - rank(b.status) || a.peso_total_kg - b.peso_total_kg;
    });

  const buildReportParams = () => {
    const p = new URLSearchParams();
    if (fFornecedor) p.set("fornecedor_id", fFornecedor);
    if (fTipo) p.set("tipo", fTipo);
    if (fCor) p.set("cor", fCor);
    if (fStatus) p.set("status", fStatus);
    p.set("mostrar", reportFiltro);
    return p.toString();
  };
  const abrirRelatorio = () => {
    setShowReport(false);
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/relatorio-estoque/pdf?${buildReportParams()}`, "_blank");
  };
  const compartilharRelatorio = () => {
    const qs = buildReportParams();
    setShowReport(false);
    sharePdf(`/relatorio-estoque/pdf?${qs}`, "Relatorio-Estoque.pdf", "Relatório de Estoque");
  };

  const selectCls = "h-10 rounded-md border-2 border-slate-300 px-2 text-sm outline-none focus:border-blue-500";

  if (loading)
    return (
      <div className="flex justify-center py-20 text-slate-400">
        <Loader2 className="animate-spin" />
      </div>
    );

  return (
    <div data-testid="painel-page">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 md:text-3xl">Painel de Estoque</h1>
          <p className="mt-1 text-sm text-slate-500">Visão por tipo, cor e fornecedor — rolos e quantidade lado a lado.</p>
        </div>
        {canReport && (
        <button
          onClick={() => setShowReport(true)}
          data-testid="open-report"
          className="flex items-center gap-2 rounded-md border-2 border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-100"
        >
          <FileText size={18} /> Relatório de Estoque (PDF)
        </button>
        )}
      </div>

      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-5">
        <StatCard icon={Layers} label="Combinações" value={resumo.combinacoes ?? 0} testid="stat-combinacoes" />
        <StatCard icon={Boxes} label="Rolos disponíveis" value={resumo.total_rolos ?? 0} testid="stat-rolos" />
        <StatCard
          icon={Scale}
          label="Total em KG"
          value={`${formatNum(resumo.total_peso_kg)} kg`}
          sub="tecidos por peso"
          testid="stat-peso"
        />
        <StatCard
          icon={Scale}
          label="Total em Metros"
          value={`${formatNum(resumo.total_metros)} m`}
          sub="tecidos por metro"
          testid="stat-metros"
        />
        <StatCard icon={AlertTriangle} label="Em estoque mínimo" value={resumo.alertas_minimo ?? 0} testid="stat-alertas" />
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3 text-xs">
        {Object.entries(STATUS_META).map(([k, m]) => (
          <div key={k} className="flex items-center gap-1.5">
            <span className="h-3 w-3 rounded-sm" style={{ background: m.color }} />
            <span className="font-medium text-slate-600">{m.label}</span>
          </div>
        ))}
      </div>

      {cards.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="painel-filtros">
          <select value={fFornecedor} onChange={(e) => setFFornecedor(e.target.value)} className={selectCls} data-testid="painel-filtro-fornecedor">
            <option value="">Todos fornecedores</option>
            {fornecedores.map(([id, nome]) => <option key={id} value={id}>{nome}</option>)}
          </select>
          <select value={fTipo} onChange={(e) => setFTipo(e.target.value)} className={selectCls} data-testid="painel-filtro-tipo">
            <option value="">Todos os tipos</option>
            {tipos.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <select value={fCor} onChange={(e) => setFCor(e.target.value)} className={selectCls} data-testid="painel-filtro-cor">
            <option value="">Todas as cores</option>
            {cores.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} className={selectCls} data-testid="painel-filtro-status">
            <option value="">Todos os status</option>
            <option value="ok">OK</option>
            <option value="atencao">Atenção</option>
            <option value="baixo">Baixo</option>
            <option value="zerado">Zerado</option>
          </select>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs font-medium text-slate-500">Ordenar:</span>
            <select value={sort} onChange={(e) => setSort(e.target.value)} className={selectCls} data-testid="painel-ordenar">
              <option value="alerta">Acabando primeiro (zerados/baixos)</option>
              <option value="menor">Menor estoque → maior</option>
              <option value="maior">Maior estoque → menor</option>
              <option value="az">Tipo/Cor (A→Z)</option>
            </select>
          </div>
        </div>
      )}

      {cards.length === 0 ? (
        <div className="rounded-md border-2 border-dashed border-slate-300 bg-white p-12 text-center" data-testid="painel-empty">
          <p className="text-slate-500">Nenhuma combinação cadastrada ainda.</p>
          <p className="mt-1 text-sm text-slate-400">
            Vá em <span className="font-semibold">Cadastro</span> para criar fornecedores e combinações de tecido.
          </p>
        </div>
      ) : cardsView.length === 0 ? (
        <div className="rounded-md border-2 border-dashed border-slate-300 bg-white p-10 text-center text-slate-500" data-testid="painel-sem-resultado">
          Nenhum tecido para esse filtro.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {cardsView.map((c) => {
            const meta = STATUS_META[c.status];
            const u = unidadeLabel(c.unidade);
            return (
              <button
                key={c.combinacao_id}
                onClick={() => openDetail(c)}
                data-testid={`stock-card-${c.combinacao_id}`}
                className="flex flex-col rounded-md border-2 bg-white p-5 text-left transition-transform hover:-translate-y-0.5"
                style={{ borderColor: meta.border, borderLeftWidth: 8 }}
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="text-lg font-bold tracking-tight text-slate-900">
                      {c.tipo} {c.cor}
                    </div>
                    <div className="text-sm font-medium text-slate-500">{c.fornecedor_nome}</div>
                  </div>
                  <span className="rounded-sm px-2 py-1 text-xs font-bold uppercase" style={{ background: meta.bg, color: meta.color }}>
                    {meta.label}
                  </span>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-3">
                  <div className="rounded-md bg-slate-50 p-3">
                    <div className="text-2xl font-bold text-slate-900 font-mono">
                      {(c.pedacos || 0) > 0 ? c.rolos_disponiveis - (c.pedacos || 0) : c.rolos_disponiveis}
                    </div>
                    <div className="text-xs font-medium text-slate-500">rolos</div>
                    {(c.pedacos || 0) > 0 && (
                      <div
                        className="mt-1 inline-flex items-center gap-1 rounded-sm bg-amber-100 px-1.5 py-0.5 text-[11px] font-bold uppercase tracking-wider text-amber-800"
                        data-testid={`card-pedacos-${c.combinacao_id}`}
                      >
                        {c.pedacos} pedaço{c.pedacos > 1 ? "s" : ""}
                      </div>
                    )}
                  </div>
                  <div className="rounded-md bg-slate-50 p-3">
                    <div className="text-2xl font-bold font-mono" style={{ color: meta.color }}>
                      {formatNum(c.peso_total_kg)}
                    </div>
                    <div className="text-xs font-medium text-slate-500">{u} total</div>
                  </div>
                </div>

                {c.em_alerta_minimo && (
                  <div className="mt-3 flex items-center gap-1.5 rounded-sm bg-red-50 px-2 py-1.5 text-xs font-semibold text-red-700" data-testid={`alert-min-${c.combinacao_id}`}>
                    <AlertTriangle size={14} /> Abaixo do mínimo ({formatMedida(c.estoque_minimo_kg, c.unidade)})
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Detalhe dos rolos */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg" data-testid="rolos-detail-dialog">
          <DialogHeader>
            <DialogTitle>{selected ? `${selected.tipo} ${selected.cor} — ${selected.fornecedor_nome}` : ""}</DialogTitle>
          </DialogHeader>
          {loadingRolos ? (
            <div className="flex justify-center py-8 text-slate-400">
              <Loader2 className="animate-spin" />
            </div>
          ) : (
            <div className="max-h-80 overflow-y-auto">
              {rolos.length === 0 ? (
                <p className="py-6 text-center text-sm text-slate-500">Nenhum rolo disponível.</p>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {rolos.map((r) => (
                    <li key={r.id} className="flex items-center justify-between py-3" data-testid={`detail-rolo-${r.codigo}`}>
                      <div>
                        <span className="font-mono font-semibold text-slate-900">{r.codigo}</span>
                        {r.origem === "Devolução de Sobra" && (
                          <span className="ml-2 rounded-sm bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-700">devolução</span>
                        )}
                        {r.localizacao && <div className="text-xs text-slate-500">Local: {r.localizacao}</div>}
                      </div>
                      <span className="font-mono font-bold text-slate-700">{formatMedida(r.peso_atual_kg, r.unidade)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Diálogo de filtro do relatório */}
      <Dialog open={showReport} onOpenChange={(o) => !o && setShowReport(false)}>
        <DialogContent data-testid="report-dialog">
          <DialogHeader>
            <DialogTitle>Relatório de Estoque (PDF)</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500">Panorama organizado por Fornecedor → Tipo → Cor (alfabética), com zebra e totais. Respeita os filtros selecionados no painel.</p>
          <div>
            <label className="mb-1 block text-sm font-semibold text-slate-700">O que mostrar</label>
            <select
              className="h-12 w-full rounded-md border-2 border-slate-300 px-3 text-base outline-none focus:border-blue-500"
              value={reportFiltro}
              onChange={(e) => setReportFiltro(e.target.value)}
              data-testid="report-filtro"
            >
              {FILTROS.map((f) => (
                <option key={f.v} value={f.v}>{f.label}</option>
              ))}
            </select>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-2">
            <button
              onClick={abrirRelatorio}
              data-testid="report-print"
              className="flex h-12 items-center justify-center gap-2 rounded-md bg-slate-900 font-semibold text-white hover:bg-slate-800"
            >
              <Printer size={18} /> Abrir PDF
            </button>
            <button
              onClick={compartilharRelatorio}
              data-testid="report-share"
              className="flex h-12 items-center justify-center gap-2 rounded-md bg-blue-600 font-semibold text-white hover:bg-blue-700"
            >
              <Share2 size={18} /> Compartilhar
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
